// Centralized, easily editable content for Advance HR Technologies
import {
  Users, Briefcase, FileSpreadsheet, Calculator, ClipboardList,
  Factory, Truck, Warehouse, Wrench, ShoppingBag, HeartPulse, UtensilsCrossed,
  Monitor, HardHat, Landmark, ShoppingCart, Beer, Pill,
  ShieldCheck, Zap, Target, BadgeCheck, Globe, Headphones, Award, Sparkles,
} from "lucide-react";

export const COMPANY = {
  name: "Advance HR Technologies",
  tagline: "Your Trusted Recruitment & Workforce Solutions Partner",
  phone: "+91 79772 61570",
  phoneRaw: "+917977261570",
  candidatePhone: "+91 88506 61570",
  candidatePhoneRaw: "+918850661570",
  whatsapp: "918850661570",
  email: "info@advancehrtech.com",
  contactPerson: "Amitkumar Verma",
  address: {
    line1: "04, B Wing, Ground Floor",
    line2: "Manas Sarovar CHSL, Near M K College",
    city: "Kalyan West",
    state: "Maharashtra",
    country: "India",
  },
  social: {
    facebook: "https://www.facebook.com/advancehrtechnologies",
    instagram: "https://www.instagram.com/advancehrtechnologies",
    linkedin: "https://www.linkedin.com/company/advance-hr-technologies",
  },
};

export const SERVICES = [
  {
    slug: "recruitment",
    icon: Users,
    title: "Recruitment Services",
    short: "End-to-end talent acquisition across permanent, executive, bulk, blue-collar & white-collar hiring.",
    bullets: [
      "Permanent Hiring",
      "Executive Search",
      "Bulk Hiring",
      "Campus Recruitment",
      "Blue-Collar Recruitment",
      "White-Collar Recruitment",
      "Leadership Hiring",
    ],
    description:
      "From entry-level workforce to CXO-level leadership, our recruitment specialists deliver pre-vetted candidates aligned to your role, culture and timeline. PAN India network across 14+ industries.",
  },
  {
    slug: "contract-staffing",
    icon: Briefcase,
    title: "Contract Staffing Services",
    short: "Temporary, project-based and flexi staffing with full compliance — scale your workforce on demand.",
    bullets: [
      "Temporary Staffing",
      "Project Staffing",
      "Flexi Staffing",
      "Workforce Outsourcing",
    ],
    benefits: [
      "Reduced hiring cost",
      "Faster deployment",
      "Compliance management",
      "Workforce flexibility",
    ],
    description:
      "Deploy qualified contractual workforce within days, not weeks. We handle onboarding, payroll, and statutory compliance — you focus on operations.",
  },
  {
    slug: "third-party-payroll",
    icon: FileSpreadsheet,
    title: "Third Party Payroll Management",
    short: "Full payroll outsourcing — onboarding to exit — with airtight PF, ESIC, PT & labour law compliance.",
    bullets: [
      "Employee Onboarding",
      "Payroll Administration",
      "Salary Processing",
      "Statutory Compliance",
      "Employee Records",
      "Attendance Integration",
      "Exit Formalities",
    ],
    compliance: ["PF", "ESIC", "Professional Tax", "Labour Law Compliance"],
    description:
      "Move employees onto our payroll while retaining full operational control. Reduce administrative overhead and audit risk with a dedicated compliance team.",
  },
  {
    slug: "payroll-processing",
    icon: Calculator,
    title: "Payroll Processing Services",
    short: "Accurate, on-time salary processing with TDS, F&F, leave & compliance reporting baked in.",
    bullets: [
      "Salary Calculation",
      "Payslip Generation",
      "TDS Computation",
      "Full & Final Settlement",
      "Leave Management",
      "Attendance Processing",
      "Compliance Reports",
    ],
    description:
      "A managed payroll bureau service that scales from 10 to 10,000 employees with secure data handling and monthly compliance dashboards.",
  },
  {
    slug: "hr-consultancy",
    icon: ClipboardList,
    title: "HR Consultancy Services",
    short: "Strategic HR advisory — from policy design to performance frameworks and compliance audits.",
    bullets: [
      "HR Policy Development",
      "Organization Structure",
      "Performance Management",
      "HR Audit",
      "Compliance Consulting",
      "HR Documentation",
      "Employee Engagement",
    ],
    description:
      "Build a high-performance organization with frameworks designed by seasoned HR practitioners. Customized for Indian SMEs, startups and enterprises.",
  },
];

export const WHY_CHOOSE_US = [
  { icon: BadgeCheck, title: "Professional HR Experts", text: "Seasoned recruiters and HR specialists across functions." },
  { icon: Zap, title: "Fast Hiring", text: "Average TAT of 7–15 days for most roles, pan India." },
  { icon: Target, title: "Cost Effective", text: "Transparent pricing with measurable ROI on every hire." },
  { icon: ShieldCheck, title: "Compliance Driven", text: "PF, ESIC, PT & labour law — fully managed for you." },
  { icon: Sparkles, title: "Customized Solutions", text: "Workforce models tailored to your industry and scale." },
  { icon: Globe, title: "PAN India Network", text: "Source talent across metros, tier-2 and tier-3 cities." },
  { icon: Headphones, title: "Dedicated Support", text: "Single point of contact + escalation matrix for every client." },
  { icon: Award, title: "Quality Candidates", text: "Multi-stage screening, BGV, and skills validation." },
];

export const INDUSTRIES = [
  { icon: Factory, name: "Manufacturing" },
  { icon: Wrench, name: "Engineering" },
  { icon: Truck, name: "Automobile" },
  { icon: Warehouse, name: "Warehousing" },
  { icon: Truck, name: "Logistics" },
  { icon: ShoppingBag, name: "Retail" },
  { icon: HeartPulse, name: "Healthcare" },
  { icon: UtensilsCrossed, name: "Hospitality" },
  { icon: Monitor, name: "IT & ITES" },
  { icon: HardHat, name: "Construction" },
  { icon: Landmark, name: "Banking & BFSI" },
  { icon: ShoppingCart, name: "E-commerce" },
  { icon: Beer, name: "FMCG" },
  { icon: Pill, name: "Pharmaceutical" },
];

export const HIRING_PROCESS = [
  { step: "01", title: "Requirement Analysis", text: "Deep discovery — role, skills, culture fit, compensation benchmarks." },
  { step: "02", title: "Candidate Sourcing", text: "Database, referrals, social, PAN India network activation." },
  { step: "03", title: "Screening", text: "Resume + telephonic + skill assessment by domain recruiters." },
  { step: "04", title: "Interview Coordination", text: "Calendar management, feedback loops, candidate experience." },
  { step: "05", title: "Selection", text: "Offer rollout, negotiation, BGV and document verification." },
  { step: "06", title: "Joining", text: "Onboarding support and seamless documentation handover." },
  { step: "07", title: "Post Joining Support", text: "Replacement guarantee and continuous account management." },
];

export const STATS = [
  { value: 1000, suffix: "+", label: "Candidates Placed" },
  { value: 100, suffix: "+", label: "Corporate Clients" },
  { value: 14, suffix: "+", label: "Industries Served" },
  { value: 95, suffix: "%", label: "Client Satisfaction" },
];

export const TESTIMONIALS = [
  {
    name: "Rajeev Menon",
    role: "Head of HR",
    company: "Sterling Industries Pvt. Ltd.",
    quote:
      "Advance HR Technologies has been a strategic partner for our blue-collar hiring across 4 plants. Their turnaround time and compliance discipline are exceptional.",
  },
  {
    name: "Priya Subramaniam",
    role: "VP - People & Culture",
    company: "Northline Logistics India",
    quote:
      "Onboarding 200+ warehouse associates in under three weeks was seamless. The team understood our SLA expectations and delivered consistently.",
  },
  {
    name: "Aniket Deshpande",
    role: "Director - Operations",
    company: "Vector Engineering Works",
    quote:
      "Their payroll outsourcing freed up our internal bandwidth significantly. Zero compliance issues across two audit cycles.",
  },
  {
    name: "Meera Kapoor",
    role: "Founder & CEO",
    company: "Brightline Retail Co.",
    quote:
      "As a growing retail brand, we needed a partner who could scale with us. Advance HR's flexi staffing model is exactly what mid-market businesses need.",
  },
  {
    name: "Sandeep Iyer",
    role: "CHRO",
    company: "Pinnacle Hospitality Group",
    quote:
      "Quality of candidates is consistently high. Their leadership search team helped us close two CXO mandates in record time.",
  },
];

export const FAQS = [
  {
    q: "What types of recruitment services do you offer?",
    a: "We offer permanent hiring, executive search, bulk hiring, campus recruitment, blue-collar and white-collar recruitment, and leadership hiring across 14+ industries pan India.",
  },
  {
    q: "How is third party payroll different from contract staffing?",
    a: "In third party payroll, employees are on our payroll while working under your operational supervision. Contract staffing is a broader engagement that may include sourcing, deployment and managed services for temporary or project-based workforce.",
  },
  {
    q: "Which compliances do you handle for payroll?",
    a: "We manage PF, ESIC, Professional Tax, TDS, gratuity, bonus, minimum wages and full Labour Law compliance, with monthly reporting and audit-ready records.",
  },
  {
    q: "What is the typical turnaround time for hiring?",
    a: "Most non-leadership roles are closed in 7–15 working days. Bulk hiring mandates are scoped with milestone-based SLAs, and leadership roles typically take 30–60 days.",
  },
  {
    q: "Do you offer a replacement guarantee?",
    a: "Yes. We offer a standard 90-day replacement guarantee on permanent placements, with extended terms available on enterprise contracts.",
  },
  {
    q: "Can you handle PAN India hiring requirements?",
    a: "Absolutely. Our recruiter network spans metros, tier-2 and tier-3 cities across India, with strong sourcing depth in Maharashtra, Gujarat, NCR, Karnataka, Tamil Nadu and Telangana.",
  },
  {
    q: "Which industries do you specialize in?",
    a: "Manufacturing, engineering, automobile, warehousing, logistics, retail, healthcare, hospitality, IT, construction, BFSI, e-commerce, FMCG and pharmaceuticals.",
  },
  {
    q: "How is your pricing structured?",
    a: "Recruitment is typically priced as a percentage of CTC for permanent roles. Staffing and payroll are priced on a per-employee per-month basis. Custom enterprise pricing is available.",
  },
  {
    q: "Do you serve startups and SMEs?",
    a: "Yes. We have flexible engagement models specifically designed for startups and SMEs — including pay-on-success recruitment and modular HR consulting.",
  },
  {
    q: "How do I get started?",
    a: "Simply request a free consultation via the form on this site or call us at +91 79772 61570. Our team will respond within one business day with a tailored proposal.",
  },
];
